import React, { useState } from 'react';
import { CustomerSection } from './sections/CustomerSection';
import { PricingSection } from './sections/PricingSection';
import { TimelineSection } from './sections/TimelineSection';
import { DetailsSection } from './sections/DetailsSection';
import { ProposalNavigation } from './ProposalNavigation';
import { ProposalFormData } from '../types';
import { ProposalRecord, proposalService } from '../services/proposalService';
import { buildPricingSheetData, buildInternalSplitData } from '../utils/pricingSheetData';
import { generatePricingSheetPDFBlob } from './PricingSheetPDF';
import { generateInternalSplitPDFBlob } from './InternalSplitPDF';

interface ProposalFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ProposalFormData) => void;
  initialData?: ProposalRecord;
}

const EMPTY_FORM: ProposalFormData = {
  customerName: '',
  customerAttn: '',
  projectTitle: '',
  customerEmail: '',
  customerPhone: '',
  customerAddressLine1: '',
  customerAddressLine2: '',
  customerCity: '',
  customerState: '',
  customerPostalCode: '',
  customerCountry: '',
  totalValue: '',
  startDate: '',
  endDate: '',
  duration: { value: '', unit: 'months' },
  description: '',
  paymentTerms: '',
  validityPeriod: '30',
  milestones: [],
  selectedProducts: {},
  includePlatform: true,
  numberOfUsers: 0,
  retentionTier: null,
  retentionYears: 0,
  customerManagedCloud: false,
  platformOverrides: {},
  retentionOverrides: {},
};

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

export const ProposalForm: React.FC<ProposalFormProps> = ({ isOpen, onClose, onSubmit, initialData }) => {
  const [currentSection, setCurrentSection] = useState<'customer' | 'pricing' | 'timeline' | 'details'>('customer');
  const [formData, setFormData] = useState<ProposalFormData>(
    initialData?.formData ? (initialData.formData as unknown as ProposalFormData) : EMPTY_FORM
  );
  // Rehydrate the last saved pricing so an opened proposal shows its numbers
  // immediately, without forcing a recalculation.
  const [calculatedTotal, setCalculatedTotal] = useState(initialData?.totalPrice ?? 0);
  const [priceBreakdown, setPriceBreakdown] = useState<unknown>(initialData?.breakdownData ?? null);
  const [calculatedRefNumber, setCalculatedRefNumber] = useState(initialData?.refNumber ?? '');
  const [isCreating, setIsCreating] = useState(false);
  const [showVersionDialog, setShowVersionDialog] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePriceCalculated = (total: number, breakdown: unknown, refNumber: string) => {
    setCalculatedTotal(total);
    setPriceBreakdown(breakdown);
    setCalculatedRefNumber(refNumber);
  };

  const buildPdfPayload = async () => {
    const sheetData = buildPricingSheetData(formData, priceBreakdown as never, calculatedTotal, calculatedRefNumber || undefined);
    const splitData = buildInternalSplitData(formData, priceBreakdown as never, calculatedTotal, calculatedRefNumber || undefined);
    // Generate sequentially — react-pdf uses a singleton WASM config that
    // cannot handle two concurrent renders (causes BindingError).
    const customerBlob = await generatePricingSheetPDFBlob(sheetData);
    const internalBlob = await generateInternalSplitPDFBlob(splitData);
    const [customerPdfBase64, internalPdfBase64] = await Promise.all([
      blobToBase64(customerBlob),
      blobToBase64(internalBlob),
    ]);
    return { sheetData, customerPdfBase64, internalPdfBase64 };
  };

  const handleSaveNewVersion = async () => {
    setShowVersionDialog(false);
    setIsCreating(true);
    try {
      const { sheetData, customerPdfBase64, internalPdfBase64 } = await buildPdfPayload();
      await proposalService.saveProposal({
        groupId: initialData?.groupId,
        refNumber: calculatedRefNumber || sheetData.referenceNumber,
        customerName: formData.customerName || undefined,
        projectTitle: formData.projectTitle || undefined,
        totalPrice: calculatedTotal,
        formData: formData as unknown as object,
        breakdownData: priceBreakdown as object ?? undefined,
        customerPdfBase64,
        internalPdfBase64,
      });
      onSubmit(formData);
      onClose();
    } catch (err) {
      console.error('Failed to save new version:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleOverwriteCurrent = async () => {
    if (!initialData) return;
    setShowVersionDialog(false);
    setIsCreating(true);
    try {
      const { sheetData, customerPdfBase64, internalPdfBase64 } = await buildPdfPayload();
      await proposalService.overwriteProposal(initialData.id, {
        groupId: initialData.groupId,
        refNumber: calculatedRefNumber || sheetData.referenceNumber,
        customerName: formData.customerName || undefined,
        projectTitle: formData.projectTitle || undefined,
        totalPrice: calculatedTotal,
        formData: formData as unknown as object,
        breakdownData: priceBreakdown as object ?? undefined,
        customerPdfBase64,
        internalPdfBase64,
      });
      onSubmit(formData);
      onClose();
    } catch (err) {
      console.error('Failed to overwrite proposal:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateProposal = async () => {
    if (calculatedTotal <= 0 || isCreating) return;
    if (initialData) {
      // editing — ask user which save mode they want
      setShowVersionDialog(true);
    } else {
      setIsCreating(true);
      try {
        const { sheetData, customerPdfBase64, internalPdfBase64 } = await buildPdfPayload();
        await proposalService.saveProposal({
          refNumber: calculatedRefNumber || sheetData.referenceNumber,
          customerName: formData.customerName || undefined,
          projectTitle: formData.projectTitle || undefined,
          totalPrice: calculatedTotal,
          formData: formData as unknown as object,
          breakdownData: priceBreakdown as object ?? undefined,
          customerPdfBase64,
          internalPdfBase64,
        });
        onSubmit(formData);
        onClose();
      } catch (err) {
        console.error('Failed to create proposal:', err);
      } finally {
        setIsCreating(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-blueprint-blue/50 backdrop-blur-sm z-50">
      <div className="flex h-screen">
        {/* Sidebar */}
        <div className="w-64 bg-white border-r border-[#E2E5EA] p-6">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-lg font-semibold font-heading text-blueprint-blue">
              {initialData ? `Edit Proposal (v${initialData.version})` : 'New Proposal'}
            </h2>
            <button onClick={onClose} className="text-heritage-grey hover:text-blueprint-blue">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <ProposalNavigation
            activeSection={currentSection}
            onSectionChange={setCurrentSection as (sectionId: string) => void}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col bg-page-bg">
          <div className="flex-1 overflow-auto">
            <div className="max-w-[1400px] mx-auto p-8">
              {currentSection === 'customer' && <CustomerSection formData={formData} onChange={handleInputChange} />}
              {currentSection === 'pricing' && (
                <PricingSection
                  formData={formData}
                  onFormDataChange={setFormData}
                  onPriceCalculated={handlePriceCalculated}
                  initialTotal={calculatedTotal}
                  initialBreakdown={priceBreakdown}
                  initialRefNumber={calculatedRefNumber}
                />
              )}
              {currentSection === 'timeline' && <TimelineSection formData={formData} onChange={handleInputChange} />}
              {currentSection === 'details' && <DetailsSection formData={formData} onChange={handleInputChange} />}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex-shrink-0 border-t border-[#E2E5EA] bg-white p-4">
            <div className="max-w-[1400px] mx-auto flex justify-between items-center">
              <button
                onClick={onClose}
                className="px-4 py-2 text-text-grey border border-[#E2E5EA] rounded-lg hover:bg-page-bg"
              >
                Cancel
              </button>
              <div className="flex items-center gap-3">
                {calculatedTotal <= 0 && (
                  <span className="text-sm text-heritage-grey">Calculate price first to save</span>
                )}
                <button
                  onClick={handleCreateProposal}
                  disabled={calculatedTotal <= 0 || isCreating}
                  className="px-4 py-2 bg-readable-red text-white rounded-lg hover:bg-hero-red-70 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? 'Saving...' : (initialData ? 'Save...' : 'Create Proposal')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Version choice dialog */}
      {showVersionDialog && initialData && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold font-heading text-blueprint-blue mb-1">How would you like to save?</h3>
            <p className="text-sm text-heritage-grey mb-6">
              You are editing <span className="font-medium">v{initialData.version}</span> of this proposal.
            </p>
            <div className="space-y-3">
              <button
                onClick={handleSaveNewVersion}
                className="w-full text-left px-4 py-3 rounded-lg border-2 border-hero-red-20 hover:border-readable-red hover:bg-hero-red-10 transition-colors group"
              >
                <p className="font-medium text-text-grey group-hover:text-readable-red">
                  Save as new version (v{initialData.version + 1})
                </p>
                <p className="text-xs text-heritage-grey mt-0.5">
                  Creates v{initialData.version + 1} and keeps v{initialData.version} unchanged in history.
                </p>
              </button>
              <button
                onClick={handleOverwriteCurrent}
                className="w-full text-left px-4 py-3 rounded-lg border-2 border-[#E2E5EA] hover:border-heritage-grey hover:bg-page-bg transition-colors group"
              >
                <p className="font-medium text-text-grey">
                  Overwrite current version (v{initialData.version})
                </p>
                <p className="text-xs text-heritage-grey mt-0.5">
                  Replaces v{initialData.version} in place. No new version is created.
                </p>
              </button>
            </div>
            <button
              onClick={() => setShowVersionDialog(false)}
              className="mt-4 w-full py-2 text-sm text-heritage-grey hover:text-blueprint-blue"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
